/* Shrink a label photo on the phone before it is uploaded.

   A phone camera photo is 3-8 MB; the server takes 12 MB per request in all,
   and the Analyse and Compare screens send two to six photos. Label text
   reads just as well at 2200 px on the long side, which brings a photo down
   to a few hundred kilobytes, so the upload is fast and never too large. */

const MAX_SIDE = 2200;
const QUALITY = 0.88;
/** Photos already this small are sent untouched. */
const SMALL_ENOUGH = 900 * 1024;

async function decode(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if ("createImageBitmap" in window) {
    try {
      // Honour the camera's rotation flag, so the photo is not sent sideways.
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      /* fall back to an <img> below */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** The photo as a JPEG at most MAX_SIDE px on its long side. Returns the
 *  original if it is already small, or if the browser cannot decode it. */
export async function shrinkPhoto(file: File): Promise<File> {
  if (file.size <= SMALL_ENOUGH) return file;
  let image: ImageBitmap | HTMLImageElement;
  try {
    image = await decode(file);
  } catch {
    return file;
  }
  const width = "naturalWidth" in image ? image.naturalWidth : image.width;
  const height = "naturalHeight" in image ? image.naturalHeight : image.height;
  const scale = Math.min(1, MAX_SIDE / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const ctx = canvas.getContext("2d");
  if (!ctx) return file;
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  if ("close" in image) image.close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", QUALITY));
  if (!blob || blob.size >= file.size) return file;
  const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
  return new File([blob], name, { type: "image/jpeg", lastModified: file.lastModified });
}
